import React, { useContext, useRef, useState } from "react";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Textarea,
    useMediaQuery,
} from "@chakra-ui/react";
import { Content } from "../../../common/types";
import { ExternalLink } from "../../../common/components/Links";
import { ContentActionsButtons } from "../../Contents/components/ContentActions";
import ContentContext from "../../../common/context/ContentContext";
import { debounce } from "../../../common/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import Editor from "../../Contents/components/Editor";

export type DataInput = Pick<Content, "title" | "content" | "url">;
const DEBOUNCE_TIME = 60_000;

interface OwnProps {
    titleRef: React.MutableRefObject<string>;
    contentRef: React.MutableRefObject<string>;
    keyConceptsRef: React.MutableRefObject<string>;
    openQuestionsRef: React.MutableRefObject<string>;
    isAllSavedRef: React.MutableRefObject<boolean>;
    onLinkContent: () => void;
    patchContent: (payload?: object) => void;
}

const ContentForm = ({
    titleRef,
    contentRef,
    keyConceptsRef,
    openQuestionsRef,
    isAllSavedRef,
    onLinkContent,
    patchContent,
}: OwnProps) => {
    const content = useContext(ContentContext);
    const changesNotified = useRef(false);
    const [showMetaContent, setShowMetaContent] = useState(
        !!content?.keyConcepts || !!content?.openQuestions
    );
    const [isAtLeastMedium] = useMediaQuery("(min-width: 48em)");
    const [isLarge] = useMediaQuery("(min-width: 1200px)");

    const notifyChanges = () => {
        if (!changesNotified.current) {
            changesNotified.current = true;
            debounce(() => {
                patchContent({
                    title: titleRef.current,
                    content: contentRef.current,
                    keyConcepts: keyConceptsRef.current,
                    openQuestions: openQuestionsRef.current,
                });
                changesNotified.current = false;
            }, DEBOUNCE_TIME);
        }
    };

    const onChangeText = (
        { target }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ref: React.MutableRefObject<string>
    ) => {
        ref.current = target.value;
        isAllSavedRef.current = false;
        notifyChanges();
    };

    const toolbarOptions = [
        ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'script': 'sub' }, { 'script': 'super' }],      // superscript/subscript
        [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent
        [{ 'header': [1, 2, 3, false] }],
        [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
        [{ 'align': [] }],
        ['clean'],                                         // remove formatting button
        ['link', 'image']
    ];



    const MetaContentAccordion = () => (
        <Accordion allowToggle defaultIndex={0}>
            <AccordionItem border={0}>
                <>
                    <AccordionButton>
                        <FormLabel fontSize="xl" flex={1}>
                            Key concepts {content?.keyConcepts && "*"}
                        </FormLabel>
                        <AccordionIcon />
                    </AccordionButton>
                </>
                <AccordionPanel>
                    <Textarea
                        placeholder="Write down main ideas, important people or dates, key points"
                        rows={20}
                        resize="none"
                        defaultValue={keyConceptsRef.current}
                        onChange={(e) => {
                            onChangeText(e, keyConceptsRef);
                        }}
                    />
                </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
                <>
                    <AccordionButton>
                        <FormLabel fontSize="xl" flex={1}>
                            Open Questions {content?.openQuestions && "*"}
                        </FormLabel>
                        <AccordionIcon />
                    </AccordionButton>
                </>
                <AccordionPanel>
                    <Textarea
                        placeholder="Do I have to search for something else and go beyond this content?"
                        rows={20}
                        resize="none"
                        defaultValue={openQuestionsRef.current}
                        onChange={(e) => {
                            onChangeText(e, openQuestionsRef);
                        }}
                    />
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    );

    const ToggleMetaContentButton = () => (
        <Button
            variant="ghost"
            leftIcon={
                <FontAwesomeIcon
                    icon={showMetaContent ? faChevronLeft : faChevronRight}
                />
            }
            onClick={() => {
                setShowMetaContent(showMetaContent ? false : true);
            }}
        >
            Key concepts & Questions
        </Button>
    );

    return (
        <Box marginTop={2}>
            <FormControl isRequired marginTop={10} marginBottom={5}>
                <Input
                    variant="flushed"
                    fontSize={{ base: "xl", md: "3xl" }}
                    type="text"
                    placeholder="Add a Title"
                    _placeholder={{
                        color: "black",
                        fontWeight: "bold",
                    }}
                    isRequired
                    defaultValue={content!.title}
                    onChange={(e) => {
                        onChangeText(e, titleRef);
                    }}
                />
            </FormControl>
            <FormControl>
                {content!.url && <ExternalLink url={content!.url} />}
            </FormControl>
            <FormControl>
                <ContentActionsButtons onLinkContent={onLinkContent} />
            </FormControl>
            <ToggleMetaContentButton />
            <HStack w="100%" alignItems="flex-start">
                <Box
                    w={isAtLeastMedium ? (isLarge ? "30%" : "50%") : "96%"}
                    hidden={!showMetaContent}
                >
                    <MetaContentAccordion />
                </Box>
                <Box
                    w={isAtLeastMedium && showMetaContent ? ( isLarge ? "70%" : "50%" ) : "96%"}
                    hidden={!isAtLeastMedium && showMetaContent}
                >
                    <FormControl>
                        <FormLabel fontSize="2xl">Notes</FormLabel>
                        <Editor defaultValue={content?.content ?? ""} onChange={(innerHTML: string) => {
                            contentRef.current = innerHTML;
                            notifyChanges();
                        }} />
                    </FormControl>
                </Box>
            </HStack>
        </Box>
    );
};

export default ContentForm;
